import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';

function useCardFadeUp(delay: number, resetKey: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible(false);
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay, resetKey]);
  return { ref, visible };
}

function LiftCard({ children, className, style, onMouseEnter, onMouseLeave }: { children: React.ReactNode; className?: string; style?: React.CSSProperties; onMouseEnter?: () => void; onMouseLeave?: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={className ?? ''}
      style={{
        ...style,
        transform: hovered ? 'translateY(-6px) scale(1.015)' : 'translateY(0) scale(1)',
        transition: hovered ? 'transform 0.18s cubic-bezier(.22,1,.36,1)' : 'transform 0.40s cubic-bezier(.22,1,.36,1)',
        willChange: 'transform',
        position: 'relative',
      }}
      onMouseEnter={() => { setHovered(true); onMouseEnter?.(); }}
      onMouseLeave={() => { setHovered(false); onMouseLeave?.(); }}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ borderRadius: 'inherit', background: 'rgba(196,168,130,0.08)', opacity: hovered ? 1 : 0, transition: 'opacity 0.18s ease' }} />
      {children}
    </div>
  );
}

function GlowButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="w-full py-3 sm:py-4 rounded-xl font-black cursor-pointer whitespace-nowrap text-xs sm:text-sm tracking-wide"
      style={{
        background: hovered ? 'rgba(196,168,130,0.22)' : 'rgba(196,168,130,0.10)',
        border: `1px solid ${hovered ? 'rgba(196,168,130,0.65)' : 'rgba(196,168,130,0.35)'}`,
        color: '#c4a882',
        boxShadow: hovered ? '0 0 18px 2px rgba(196,168,130,0.18), inset 0 0 12px rgba(196,168,130,0.06)' : 'none',
        transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      {children}
    </button>
  );
}

const inputCls = 'w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 text-sm bg-[#f5f0e8] border border-[#d4c4a8] focus:ring-[#c4a882] text-[#2a2219] placeholder:text-[#9a8870]';
const textareaCls = 'w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 text-sm bg-[#f5f0e8] border border-[#d4c4a8] focus:ring-[#c4a882] text-[#2a2219] resize-none placeholder:text-[#9a8870]';

function CalendarStep({ lang }: { lang: 'en' | 'fr' }) {
  const label = lang === 'fr' ? 'Réserver une consultation virtuelle' : 'Book a virtual consultation';
  const sub = lang === 'fr' ? 'Planifiez votre appel vidéo sur notre calendrier' : 'Schedule your video call on our calendar';
  const badge = lang === 'fr' ? 'Requis' : 'Required';
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#c4a882' }}>
          <span className="text-[#0e0b07] text-xs font-bold">1</span>
        </div>
        <span className="text-sm font-semibold text-[#1a1510]">{label}</span>
        <span className="text-xs text-[#0e0b07] px-2 py-0.5 rounded-full font-medium" style={{ background: '#c4a882' }}>{badge}</span>
      </div>
      <a href="https://calendar.app.google/your-link" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border transition-colors cursor-pointer group" style={{ borderColor: 'rgba(196,168,130,0.35)', background: 'rgba(196,168,130,0.06)' }}>
        <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'rgba(196,168,130,0.15)' }}>
          <i className="ri-video-chat-line text-base text-[#c4a882]"></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#1a1510]">{label}</div>
          <div className="text-xs text-[#6a5a45]">{sub}</div>
        </div>
        <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform text-[#c4a882]"></i>
      </a>
    </div>
  );
}

function FormStepHeader({ stepNum, lang }: { stepNum: number; lang: 'en' | 'fr' }) {
  const label = lang === 'fr' ? 'Remplissez le formulaire ci-dessous' : 'Fill out the form below';
  const badge = lang === 'fr' ? 'Requis' : 'Required';
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#c4a882' }}>
        <span className="text-[#0e0b07] text-xs font-bold">{stepNum}</span>
      </div>
      <span className="text-sm font-semibold text-[#1a1510]">{label}</span>
      <span className="text-xs text-[#0e0b07] px-2 py-0.5 rounded-full font-medium" style={{ background: '#c4a882' }}>{badge}</span>
    </div>
  );
}

function SubtitleWithBold({ text, spanStyle }: { text: string; spanStyle?: React.CSSProperties }) {
  const parts = text.split('\n');
  return (
    <p className="text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed" style={{ color: 'rgba(196,168,130,0.7)' }}>
      {parts.map((line, i) =>
        i === 0
          ? <strong key={i} className="block mb-1" style={{ color: '#c4a882' }}>{line}</strong>
          : <span key={i} style={spanStyle ?? { color: 'rgba(196,168,130,0.6)' }}>{line}</span>
      )}
    </p>
  );
}

function renderLineWithHighlight(line: string, idx: number) {
  const match = line.match(/^([\s\S]*?)\[\[([\s\S]*?)\]\]([\s\S]*)$/);
  if (!match) {
    return <span key={idx} style={{ color: 'rgba(196,168,130,0.6)' }}>{line}</span>;
  }
  return (
    <span key={idx} style={{ color: 'rgba(196,168,130,0.6)' }}>
      {match[1]}{match[2]}{match[3]}
    </span>
  );
}

function SubtitleCard2({ text }: { text: string }) {
  const parts = text.split('\n');
  return (
    <p className="text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed" style={{ color: 'rgba(196,168,130,0.7)' }}>
      {parts.map((line, i) =>
        i === 0
          ? <strong key={i} className="block mb-1" style={{ color: '#c4a882' }}>{line}</strong>
          : renderLineWithHighlight(line, i)
      )}
    </p>
  );
}

export default function PricingSection() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [card1Open, setCard1Open] = useState(false);
  const [card2Open, setCard2Open] = useState(false);
  const card1 = useCardFadeUp(0, lang);
  const card2 = useCardFadeUp(160, lang);

  const cardBaseStyle = (visible: boolean): React.CSSProperties => ({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0px)' : 'translateY(40px)',
    transition: 'opacity 0.55s cubic-bezier(0.16,1,0.3,1), transform 0.55s cubic-bezier(0.16,1,0.3,1)',
  });

  const t = {
    en: {
      h2: 'Our Services',
      card1Badge: 'Not sure what you want?',
      card1Title: 'Matchmaking Lab',
      card1Price: 'Our Gift to You',
      card1PriceNote: 'per service, Includes a 60 min virtual consultation',
      card1Subtitle: 'Every new vehicle model available, suitable for you.\nMove forward with clarity and confidence. Answer a few simple questions and our platform delivers a tailored shortlist of all new vehicles that fit your needs, budget, and preferences — with all the details and expert recommendations.',
      card1Features: ['Needs & wants analysis', 'Personalized vehicle model recommendations based on your budget', 'Easy-to-compare specs and key details', 'Fuel efficiency or EV range insights'],
      card1CTA: 'Start Matchmaking Analysis',
      card2Badge: 'Already know what you want?',
      card2Title: 'Sourcing, Negotiation & Management',
      card2Price: '$299',
      card2PriceNote: 'Includes a 60 MIN virtual consultation. 1/2 paid up front, the other 1/2 paid after delivery.',
      card2Subtitle: 'We handle everything from search to delivery.\nOnce you know what model you want, we locate the right vehicle, negotiate the best deal, [[discuss and educate you on financing VS leasing VS outright purchasing]] and manage the entire process from A to Zero — saving you time, money, and effort.',
      card2Features: ['up to 60 minute consultation to confirm your exact vehicle', 'We find the best options from dealers and private sellers. New or pre-owned.', 'We perform negotiations with various sellers to get you the best price.', 'We provide you with clear & educational finance or lease or outright purchase options and comparisons.', 'We review of incentives and special offers.', 'We provide warranty and protection plan guidance.', 'Pre-purchase inspection coordination', 'Delivery and logistics management', 'Follow-up support after purchase'],
      card2CTA: 'Start Sourcing Process',
      m1Title: 'The Discovery', m1Subtitle: 'Please complete both steps below to get started.', m1Success: 'Thanks! We look forward to meeting you.',
      m1Name: 'Full Name *', m1NamePh: 'John Smith', m1Email: 'Email Address *', m1EmailPh: 'john@example.com',
      m1Phone: 'Phone Number *', m1PhonePh: '+1 (555) 000-0000', m1LangLabel: 'Preferred Language *', m1LangEn: 'English', m1LangFr: 'French',
      m1MessagePh: 'Let us know anything important before our meeting.', m1MaxChars: 'Maximum 500 characters', m1Submit: 'Book a virtual consultation',
      m1Consent: 'I consent to having this website store my submitted information so they can respond to my inquiry. I understand that I may be contacted using the details I provided.',
      m2Title: 'The End-to-End Service', m2Subtitle: 'Please complete both steps below to get started.', m2Success: 'Thanks! We look forward to meeting you.',
      m2Name: 'Full Name *', m2NamePh: 'John Smith', m2Email: 'Email Address *', m2EmailPh: 'john@example.com',
      m2Phone: 'Phone Number *', m2PhonePh: '+1 (555) 000-0000', m2LangLabel: 'Preferred Language *', m2LangEn: 'English', m2LangFr: 'French',
      m2MessagePh: 'Let us know anything important before our meeting.', m2MaxChars: 'Maximum 500 characters', m2Submit: 'Book a virtual consultation',
      m2Consent: 'I consent to having this website store my submitted information so they can respond to my inquiry. I understand that I may be contacted using the details I provided.',
    },
    fr: {
      h2: 'Nos Services',
      card1Badge: 'Vous ne savez pas ce que vous voulez?',
      card1Title: 'Matchmaking: Tous les modèles de véhicules neufs disponibles',
      card1Price: 'Offert Sans Frais',
      card1PriceNote: 'par service, Comprend une consultation virtuelle de 60 min',
      card1Subtitle: "Sachez exactement quoi acheter, avant d'acheter.\nAvancez avec clarté et confiance. Répondez à quelques questions et notre plateforme vous propose une liste sur mesure de tous les modèles de véhicules neufs adaptés à vos besoins, votre budget et vos préférences — avec tous les détails et spécifications de chacun.",
      card1Features: ['Analyse des besoins et des envies', 'Recommandations de modèles personnalisées selon votre budget', 'Spécifications clés faciles à comparer', "Informations sur l'efficacité énergétique ou l'autonomie VÉ"],
      card1CTA: 'Débuter le Matchmaking',
      card2Badge: 'Vous savez déjà ce que vous voulez?',
      card2Title: 'Acquisition, Négociation et Gestion',
      card2Price: '285$',
      card2PriceNote: 'Comprend une consultation virtuelle de 60 MIN. 50% payable à l\'avance, 50% à la livraison',
      card2Subtitle: "Nous gérons tout, de la recherche à la livraison.\nUne fois que vous savez quel modèle vous voulez, nous localisons le bon véhicule, négocions les meilleurs conditions, [[comparons et expliquons le financement VS la location VS l'achat]] et gérons tout le processus A à Z — vous faisant économiser temps, argent et efforts.",
      card2Features: ['Consultation de 60 minutes pour confirmer votre véhicule exact', 'Nous trouvons les meilleures options chez les concessionnaires et vendeurs privés', 'Négociation pour vous obtenir le meilleur prix', 'Comparaisons claires de financement et de location', 'Analyse des incitatifs des fabricants et offres spéciales', 'Conseils sur les garanties et plans de protection', "Coordination de l'inspection avant achat", 'Gestion de la livraison et de la logistique', "Support de suivi après l'achat"],
      card2CTA: 'Débuter l\'Acquisition',
      m1Title: 'La Découverte', m1Subtitle: 'Veuillez compléter les deux étapes ci-dessous pour commencer.', m1Success: 'Merci! Nous avons hâte de vous rencontrer.',
      m1Name: 'Nom Complet *', m1NamePh: 'Jean Dupont', m1Email: 'Adresse Courriel *', m1EmailPh: 'jean@exemple.com',
      m1Phone: 'Numéro de Téléphone *', m1PhonePh: '+2 (555) 000-0000', m1LangLabel: 'Langue de communication préférée *', m1LangEn: 'Anglais', m1LangFr: 'Français',
      m1MessagePh: 'Quelque chose à nous dire avant notre rencontre?', m1MaxChars: 'Maximum 500 caractères', m1Submit: 'Réserver une consultation virtuelle',
      m1Consent: "J'accepte que ce site web conserve les informations que j'ai soumises afin de pouvoir répondre à ma demande. Je comprends que je pourrais être contacté(e) en utilisant les coordonnées que j'ai fournies.",
      m2Title: 'Le Service Clé en Main', m2Subtitle: 'Veuillez compléter les deux étapes ci-dessous pour commencer.', m2Success: 'Merci! Nous avons hâte de vous rencontrer.',
      m2Name: 'Nom Complet *', m2NamePh: 'Jean Dupont', m2Email: 'Adresse Courriel *', m2EmailPh: 'jean@exemple.com',
      m2Phone: 'Numéro de Téléphone *', m2PhonePh: '+1 (555) 000-0000', m2LangLabel: 'Langue de communication préférée *', m2LangEn: 'Anglais', m2LangFr: 'Français',
      m2MessagePh: 'Quelque chose à nous dire avant notre rencontre?', m2MaxChars: 'Maximum 500 caractères', m2Submit: 'Réserver une consultation virtuelle',
      m2Consent: "J'accepte que ce site web conserve les informations que j'ai soumises afin de pouvoir répondre à ma demande. Je comprends que je pourrais être contacté(e) en utilisant les coordonnées que j'ai fournies.",
    },
  };

  const tx = t[lang];

  return (
    <>
      <section id="pricing" className="relative py-8 sm:py-14 lg:py-20 overflow-hidden" style={{ background: '#101018' }}>
        {/* BG texture */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(196,168,130,0.04) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 30%, rgba(196,168,130,0.04) 0%, transparent 70%)' }} />
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.2), transparent)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-20">
          <div className="text-left sm:text-center mb-6 sm:mb-12">
            <h2 className="text-[22px] sm:text-[36px] lg:text-6xl font-black text-white whitespace-nowrap tracking-tight" style={{ fontFamily: 'Satoshi, Inter, sans-serif', letterSpacing: '-0.02em' }}>
              {tx.h2}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8">
            {/* Card 1 */}
            <div ref={card1.ref} style={cardBaseStyle(card1.visible)} className="relative pt-5 sm:pt-0">
              <div className="absolute top-1 sm:-top-3 left-4 sm:left-6 z-10 px-3 sm:px-4 py-1 sm:py-1.5 text-[#0e0b07] text-[9px] sm:text-xs font-bold rounded-full whitespace-nowrap" style={{ background: '#c4a882' }}>
                {tx.card1Badge}
              </div>
              <LiftCard className="relative rounded-2xl h-full flex flex-col" style={{ background: 'rgba(8,8,20,0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(196,168,130,0.18)', boxShadow: '0 8px 48px rgba(0,0,0,0.4), inset 0 1px 0 rgba(196,168,130,0.08)' }}>
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.4), transparent)' }} />
                <div className="relative z-10 p-5 sm:p-8 flex flex-col h-full">
                  <div className="border-b pb-5 sm:pb-7 mb-5 sm:mb-7" style={{ borderColor: 'rgba(196,168,130,0.1)' }}>
                    <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(196,168,130,0.10)', border: '1px solid rgba(196,168,130,0.25)' }}>
                        <i className="ri-search-eye-line text-sm sm:text-lg text-[#c4a882]"></i>
                      </div>
                      <h3 className="text-[15px] sm:text-[24px] font-black text-white leading-tight pt-0.5 sm:pt-1" style={{ fontFamily: 'Satoshi, Inter, sans-serif', letterSpacing: '-0.02em' }}>
                        {tx.card1Title}
                      </h3>
                    </div>
                    <div className="flex flex-col items-start gap-0.5">
                      <span className={`font-black text-white ${lang === 'fr' ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'}`}>{tx.card1Price}</span>
                      <span className="text-[10px] sm:text-xs font-semibold tracking-wide uppercase" style={{ color: '#c4a882' }}>
                        {lang === 'fr' ? 'pour une durée limitée seulement' : 'for a limited time only'}
                      </span>
                    </div>
                  </div>
                  <div className="mb-5 sm:mb-7 flex-1">
                    <SubtitleWithBold text={tx.card1Subtitle} />
                    <button onClick={() => setCard1Open(prev => !prev)} className="flex items-center gap-2 text-[#c4a882] text-xs sm:text-sm font-bold mb-4 cursor-pointer hover:opacity-80 transition-opacity">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full border-2 border-[#c4a882]">
                        <i className={`ri-${card1Open ? 'subtract' : 'add'}-line text-[10px]`}></i>
                      </div>
                      {card1Open ? (lang === 'fr' ? "Masquer ce qui est inclus" : "Hide what's included") : (lang === 'fr' ? 'Voir ce qui est inclus' : "See what's included")}
                    </button>
                    <div style={{ maxHeight: card1Open ? `${tx.card1Features.length * 48}px` : '0px', overflow: 'hidden', transition: 'max-height 0.4s ease-in-out' }}>
                      <div className="space-y-2 sm:space-y-3 pb-1">
                        {tx.card1Features.map((feature, i) => (
                          <div key={i} className="flex items-start gap-2 sm:gap-3">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(196,168,130,0.12)', border: '1px solid rgba(196,168,130,0.2)' }}>
                              <i className="ri-check-line text-[#c4a882] text-[9px] sm:text-xs"></i>
                            </div>
                            <span className="text-xs sm:text-sm leading-relaxed" style={{ color: 'rgba(196,168,130,0.65)' }}>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <GlowButton onClick={() => navigate('/matchmaking-platform')}>
                    {tx.card1CTA}
                  </GlowButton>
                </div>
              </LiftCard>
            </div>

            {/* Card 2 */}
            <div ref={card2.ref} style={cardBaseStyle(card2.visible)} className="relative pt-5 sm:pt-0">
              <div className="absolute top-1 sm:-top-3 left-4 sm:left-6 z-10 px-3 sm:px-4 py-1 sm:py-1.5 text-[#0e0b07] text-[9px] sm:text-xs font-bold rounded-full whitespace-nowrap" style={{ background: '#a88c6a' }}>
                {tx.card2Badge}
              </div>
              <LiftCard className="relative rounded-2xl h-full flex flex-col" style={{ background: 'rgba(12,12,24,0.7)', backdropFilter: 'blur(20px)', border: '1px solid rgba(196,168,130,0.22)', boxShadow: '0 8px 48px rgba(0,0,0,0.4), inset 0 1px 0 rgba(196,168,130,0.1)' }}>
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.5), transparent)' }} />
                <div className="relative z-10 p-5 sm:p-8 flex flex-col h-full">
                  <div className="border-b pb-5 sm:pb-7 mb-5 sm:mb-7" style={{ borderColor: 'rgba(196,168,130,0.12)' }}>
                    <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(196,168,130,0.10)', border: '1px solid rgba(196,168,130,0.25)' }}>
                        <i className="ri-route-line text-sm sm:text-lg text-[#c4a882]"></i>
                      </div>
                      <h3 className="text-[15px] sm:text-[24px] font-black text-white leading-tight pt-0.5 sm:pt-1" style={{ fontFamily: 'Satoshi, Inter, sans-serif', letterSpacing: '-0.02em' }}>
                        {tx.card2Title}
                      </h3>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl sm:text-3xl font-black text-white">{lang === 'fr' ? '149$' : '$149'}</span>
                        <span className="text-[10px] sm:text-xs font-semibold" style={{ color: 'rgba(196,168,130,0.65)' }}>{lang === 'fr' ? "à l'avance" : 'upfront, includes 60 min virtual consultation.'}</span>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl sm:text-3xl font-black text-white">{lang === 'fr' ? '149$' : '$149'}</span>
                        <span className="text-[10px] sm:text-xs font-semibold" style={{ color: 'rgba(196,168,130,0.65)' }}>{lang === 'fr' ? 'à la livraison' : 'after delivery'}</span>
                      </div>

                    </div>
                  </div>
                  <div className="mb-5 sm:mb-7 flex-1">
                    <SubtitleCard2 text={tx.card2Subtitle} />
                    <button onClick={() => setCard2Open(prev => !prev)} className="flex items-center gap-2 text-[#c4a882] text-xs sm:text-sm font-bold mb-4 cursor-pointer hover:opacity-80 transition-opacity">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full border-2 border-[#c4a882]">
                        <i className={`ri-${card2Open ? 'subtract' : 'add'}-line text-[10px]`}></i>
                      </div>
                      {card2Open ? (lang === 'fr' ? "Masquer ce qui est inclus" : "Hide what's included") : (lang === 'fr' ? 'Voir ce qui est inclus' : "See what's included")}
                    </button>
                    <div style={{ maxHeight: card2Open ? `${tx.card2Features.length * 48}px` : '0px', overflow: 'hidden', transition: 'max-height 0.4s ease-in-out' }}>
                      <div className="space-y-2 sm:space-y-3 pb-1">
                        {tx.card2Features.map((feature, i) => (
                          <div key={i} className="flex items-start gap-2 sm:gap-3">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(196,168,130,0.12)', border: '1px solid rgba(196,168,130,0.2)' }}>
                              <i className="ri-check-line text-[#c4a882] text-[9px] sm:text-xs"></i>
                            </div>
                            <span className="text-xs sm:text-sm leading-relaxed" style={{ color: 'rgba(196,168,130,0.65)' }}>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <GlowButton onClick={() => navigate('/end-to-end-service')}>
                    {tx.card2CTA}
                  </GlowButton>
                </div>
              </LiftCard>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
